import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import SaveIcon from '@mui/icons-material/Save';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { findVariantByExactSku, apiClient } from '../../services/productapi';
import { useTransactionContext } from '../../contexts/TransactionContext';
import debounce from 'lodash/debounce';

const SaleReturnRecord = () => {
  const { triggerRefresh } = useTransactionContext();
  const [transactionType, setTransactionType] = useState('sale');
  const [marketplace, setMarketplace] = useState('internal');
  const [sku, setSku] = useState('');
  const [quantity, setQuantity] = useState(''); // Start with empty for better UX
  const [costPrice, setCostPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [variantInfo, setVariantInfo] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const marketplaceOptions = [
    { value: 'internal', label: 'Internal' },
    { value: 'amazon', label: 'Amazon' },
    { value: 'flipkart', label: 'Flipkart' },
    { value: 'myntra', label: 'Myntra' },
    { value: 'ajio', label: 'Ajio' },
    { value: 'meesho', label: 'Meesho' },
    { value: 'other', label: 'Other' }
  ];

  const transactionTypeOptions = [
    { value: 'sale', label: 'Sale', icon: <LocalShippingIcon fontSize="small" /> },
    { value: 'return', label: 'Return', icon: <KeyboardReturnIcon fontSize="small" /> },
    { value: 'purchase', label: 'Purchase', icon: <ShoppingCartIcon fontSize="small" /> }
  ];

  const lookupSkuExact = async (skuValue) => {
    if (!skuValue.trim()) {
      setVariantInfo(null);
      setError(null);
      setCostPrice(''); 
      return;
    }

    try {
      setLoading(true);
      const response = await findVariantByExactSku(skuValue.trim());
      setVariantInfo(response.data);
      setCostPrice(response.data.cost_price ? parseInt(response.data.cost_price) : '');
      setError(null);
    } catch (err) {
      console.error('Error looking up SKU:', err);
      if (err.response && err.response.status === 404) {
        setError(`No product found with SKU: ${skuValue}`);
        setCostPrice('');
      } else {
        setError('Error looking up SKU. Please try again.');
        setCostPrice('');
      }
      setVariantInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Create a debounced version of the lookup function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLookup = useCallback(
    debounce((skuValue) => lookupSkuExact(skuValue), 300),
    []
  );

  // Run the lookup when the SKU changes
  useEffect(() => {
    debouncedLookup(sku);
    return () => {
      debouncedLookup.cancel();
    };
  }, [sku, debouncedLookup]);

  const handleTransactionTypeChange = (e) => {
    const newType = e.target.value;
    setTransactionType(newType);
    
    // Reset form fields based on transaction type
    if (newType === 'purchase') {
      setMarketplace('other');
      setQuantity(''); // Set quantity to blank for purchase transactions
    } else {
      setMarketplace('internal'); // Reset to internal for sale/return
      setQuantity(''); // Keep quantity empty, it's not shown for sale/return anyway
    }
    
    // Clear any existing errors
    setError(null);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setQuantity('');
    } else {
      const numValue = parseInt(value, 10);
      setQuantity(numValue >= 1 ? numValue : 1);
    }
  };

  const handleCostPriceChange = (e) => {
    const value = e.target.value;
    // Allow decimal numbers and empty string
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCostPrice(value);
      // Clear error if user starts typing valid cost price
      if (error && error.includes('cost price')) {
        setError(null);
      }
    }
  };

  const handleSkuKeyPress = (e) => {
    if (e.key === 'Enter' && variantInfo) {
      if (transactionType === 'purchase') {
        // Focus on quantity field
        const quantityField = document.getElementById('quantity-input');
        if (quantityField) {
          quantityField.focus();
        }
      } else {
        // For sale/return, directly submit
        handleSubmit();
      }
    }
  };

  const handleQuantityKeyPress = (e) => {
    if (e.key === 'Enter' && transactionType === 'purchase') {
      // Focus on cost price field
      const costPriceField = document.getElementById('cost-price-input');
      if (costPriceField) {
        costPriceField.focus();
      }
    }
  };

  const handleCostPriceKeyPress = (e) => {
    if (e.key === 'Enter' && transactionType === 'purchase' && variantInfo && costPrice) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!sku.trim()) {
      setError('Please enter a valid SKU');
      return;
    }

    if (!variantInfo) {
      setError('Please search for a valid SKU first');
      return;
    }

    // Validation for purchase transactions
    if (transactionType === 'purchase') {
      if (!quantity || quantity === '' || parseInt(quantity) < 1 || isNaN(parseInt(quantity))) {
        setError('Please enter a valid quantity (must be a positive number)');
        return;
      }
      if (!costPrice || costPrice === '' || parseFloat(costPrice) <= 0 || isNaN(parseFloat(costPrice))) {
        setError('Please enter a valid cost price (must be a positive number)');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate quantity based on transaction type
      // For sale, quantity is negative; for purchase and return, quantity is positive
      let adjustedQuantity;
      if (transactionType === 'sale') {
        adjustedQuantity = -1; // Always -1 for sales
      } else if (transactionType === 'return') {
        adjustedQuantity = 1; // Always +1 for returns
      } else {
        adjustedQuantity = Math.abs(quantity);  // Use input quantity for purchases
      }

      const transactionData = {
        variant: variantInfo.id,
        transaction_type: transactionType,
        quantity: adjustedQuantity,
        marketplace: marketplace
      };

      // Add cost price for purchase transactions
      if (transactionType === 'purchase' && costPrice) {
        transactionData.cost_price = parseFloat(costPrice);
      }      // Submit transaction
      const response = await apiClient.post('/product/inventory-transactions/', transactionData);

      // Clear form and show success
      setSku('');
      setQuantity('');
      setCostPrice('');
      setVariantInfo(null);
      
      // Trigger refresh of the transaction table
      triggerRefresh();
      
      setSnackbar({
        open: true,
        message: `${transactionType.charAt(0).toUpperCase() + transactionType.slice(1)} transaction recorded successfully`,
        severity: 'success'
      });

      // Focus on SKU field for next entry
      setTimeout(() => {
        const skuField = document.querySelector('input[placeholder="Enter product SKU"]');
        if (skuField) {
          skuField.focus();
        }
      }, 100);
    } catch (err) {
      console.error('Error recording transaction:', err);
      setError('Failed to record transaction. Please try again.');
      
      setSnackbar({
        open: true,
        message: 'Error: Failed to record transaction',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={0}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
            <Select
              labelId="transaction-type-label"
              id="transaction-type"
              value={transactionType}
              onChange={handleTransactionTypeChange}
              label="Transaction Type"
            >
              {transactionTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {option.icon}
                    <Box sx={{ ml: 1 }}>{option.label}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="marketplace-label">Marketplace</InputLabel>
            <Select
              labelId="marketplace-label"
              id="marketplace"
              value={marketplace}
              onChange={(e) => setMarketplace(e.target.value)}
              label="Marketplace"
              disabled={transactionType === 'purchase'}
            >
              {marketplaceOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="SKU"
            variant="outlined"
            placeholder="Enter product SKU"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            onKeyPress={handleSkuKeyPress}
            InputProps={{
              endAdornment: loading && (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              )
            }}
          />
        </Grid>
        
        {transactionType === 'purchase' && (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                id="quantity-input"
                fullWidth
                label="Quantity"
                variant="outlined"
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                onKeyPress={handleQuantityKeyPress}
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                id="cost-price-input"
                fullWidth
                label="Cost Price"
                variant="outlined"
                type="number"
                value={costPrice}
                onChange={handleCostPriceChange}
                onKeyPress={handleCostPriceKeyPress}
                placeholder="Enter cost price"
                inputProps={{ 
                  step: "0.01",
                  min: "0"
                }}
              />
            </Grid>
          </>
        )}
        <Box sx={{ mx: 10,my:0.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading || !variantInfo}
            sx={{ textTransform: 'none' }}
            >
            {loading ? 'Processing...' : `Record ${transactionType}`}
            </Button>
        </Box>
      </Grid>

      {error && (
        <Box sx={{ mt: 2, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {variantInfo && (
        <Card sx={{ mt: 3, bgcolor: '#f9fafe' }} elevation={0}>
          <CardContent>
            <Grid container spacing={2}>
              {/* Image display section */}
              <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                {variantInfo.image ? (
                  <Box 
                    sx={{ 
                      height: 200, 
                      width: '100%', 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <img
                      src={variantInfo.image}
                      alt={variantInfo.product_name}
                      style={{
                        maxHeight: '100%',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      height: 150, 
                      width: '100%', 
                      bgcolor: '#e0e0e0',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '4px'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      No image available
                    </Typography>
                  </Box>
                )}
              </Grid>
              
              {/* Product details section */}
              <Grid item xs={12} sm={9}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                  Product Found:
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center',gap: '10px',flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      SKU: <strong>{variantInfo.sku}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Color: <strong>{variantInfo.color_name || 'N/A'}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current Stock: <strong>{variantInfo.stock}</strong>
                    </Typography>              
                    <Typography variant="body2" color="text.secondary">
                      Product: <strong>{variantInfo.product_name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cost Price <strong>₹{variantInfo.cost_price ? parseInt(variantInfo.cost_price) : 'N/A'}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status: <strong>{variantInfo.state}</strong>
                    </Typography>
                  </Grid> 
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SaleReturnRecord;
